import React, { useState } from 'react';
import { Mail, MapPin, Phone, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import translations from '../i18n/translations';
import './Contact.css';
 
// EmailJS credentials from environment variables
const EMAILJS_SERVICE_ID  = process.env.REACT_APP_EMAILJS_SERVICE_ID || '';
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || '';
const EMAILJS_PUBLIC_KEY  = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || '';
const TO_EMAIL = process.env.REACT_APP_TO_EMAIL || '';


async function sendEmail(formData, lang) {
  // Dynamically load EmailJS only when needed
  const emailjs = await import('@emailjs/browser').catch(() => null);

  if (!emailjs || EMAILJS_SERVICE_ID === 'YOUR_SERVICE_ID') {
    // Fallback: open default mail client if EmailJS not configured
    const subject = encodeURIComponent(`[KYCPro.AI] New message from ${formData.name}`);
    const body = encodeURIComponent(
      `Name: ${formData.name}\nEmail: ${formData.email}\nCompany: ${formData.company}\nInterest: ${formData.interest}\n\nMessage:\n${formData.message}`
    );
    window.location.href = `mailto:${TO_EMAIL}?subject=${subject}&body=${body}`;
    return { success: true, fallback: true };
  }

  await emailjs.send(
    EMAILJS_SERVICE_ID,
    EMAILJS_TEMPLATE_ID,
    {
      to_email:   TO_EMAIL,
      from_name:  formData.name,
      from_email: formData.email,
      company:    formData.company,
      interest:   formData.interest,
      message:    formData.message,
      reply_to:   formData.email,
    },
    EMAILJS_PUBLIC_KEY
  );
  return { success: true };
}

export default function Contact() {
  const emptyForm = { name: '', email: '', company: '', interest: '', message: '' };
  const [form, setForm] = useState(emptyForm);
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const { lang } = useLang();
  const t = translations[lang].contact;

  const contactInfo = [
    { icon: <Mail size={20} />, label: t.email, value: 'info@CoreNexFlow.com', href: 'mailto:info@CoreNexFlow.com' },
    { icon: <Phone size={20} />, label: t.phone, value: '+00 00 000 0000', href: 'tel:+00000000' },
    { icon: <MapPin size={20} />, label: t.hq, value: '', href: '#' },
  ];

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    try {
      await sendEmail(form, lang);
      setStatus('success');
    } catch (err) {
      console.error('Email send error:', err);
      setStatus('error');
    }
  };

  const reset = () => { setStatus('idle'); setForm(emptyForm); };

  return (
    <div className="contact">
      <section className="page-hero">
        <div className="page-hero__orb" />
        <div className="container">
          <span className="section-tag">{t.tag}</span>
          <h1 className="page-hero__title">
            {t.title1} <span className="gradient-text">{t.title2}</span>
          </h1>
          <p className="page-hero__desc">{t.desc}</p>
        </div>
      </section>

      <section className="contact-main section">
        <div className="container contact-main__inner">

          {/* Info Column */}
          <div className="contact-info">
            <h2 className="contact-info__title">{t.infoTitle}</h2>
            <p className="contact-info__desc">{t.infoDesc}</p>
            <div className="contact-info__items">
              {contactInfo.map((item, i) => (
                <a key={i} href={item.href} className="contact-info__item">
                  <div className="contact-info__item-icon">{item.icon}</div>
                  <div>
                    <span className="contact-info__item-label">{item.label}</span>
                    <span className="contact-info__item-value">{item.value}</span>
                  </div>
                </a>
              ))}
            </div>
{/* 
            <div className="contact-info__offices">
              <h3>{t.offices}</h3>
              <div className="office"><strong>Amsterdam (HQ)</strong><span>Herengracht 420, 1017 BZ</span></div>
              <div className="office"><strong>London</strong><span>22 Bishopsgate, EC2N 4BQ</span></div>
              <div className="office"><strong>Singapore</strong><span>1 Raffles Place, #20-61</span></div>
            </div> */}
          </div>

          {/* Form Column */}
          <div className="contact-form-wrap">

            {/* SUCCESS */}
            {status === 'success' && (
              <div className="contact-success">
                <div className="contact-success__circle">
                  <CheckCircle size={52} />
                </div>
                <h3>{t.successTitle}</h3>
                <p>{t.successDesc}</p>
                <p className="contact-success__email">
                  {lang === 'ar' ? 'تم الإرسال إلى' : 'Sent to'}{' '}
                  <strong>{TO_EMAIL}</strong>
                </p>
                <button className="btn-outline" onClick={reset}>{t.sendAnother}</button>
              </div>
            )}

            {/* ERROR */}
            {status === 'error' && (
              <div className="contact-error">
                <AlertCircle size={40} className="contact-error__icon" />
                <h3>{lang === 'ar' ? 'فشل الإرسال' : 'Failed to Send'}</h3>
                <p>
                  {lang === 'ar'
                    ? 'حدث خطأ. يرجى المحاولة مرة أخرى أو التواصل معنا مباشرة على '
                    : 'Something went wrong. Please try again or email us directly at '}
                  <a href={`mailto:${TO_EMAIL}`}>{TO_EMAIL}</a>
                </p>
                <button className="btn-outline" onClick={() => setStatus('idle')}>
                  {lang === 'ar' ? 'حاول مجدداً' : 'Try Again'}
                </button>
              </div>
            )}

            {/* FORM */}
            {(status === 'idle' || status === 'loading') && (
              <form className="contact-form" onSubmit={handleSubmit}>
                <h2 className="contact-form__title">{t.formTitle}</h2>

                <div className="contact-form__row">
                  <div className="form-group">
                    <label htmlFor="name">{t.name} *</label>
                    <input
                      id="name" name="name" type="text" required
                      placeholder={t.namePlaceholder}
                      value={form.name} onChange={handleChange}
                      disabled={status === 'loading'}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">{t.workEmail} *</label>
                    <input
                      id="email" name="email" type="email" required
                      placeholder={t.emailPlaceholder}
                      value={form.email} onChange={handleChange}
                      disabled={status === 'loading'}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="company">{t.company}</label>
                  <input
                    id="company" name="company" type="text"
                    placeholder={t.companyPlaceholder}
                    value={form.company} onChange={handleChange}
                    disabled={status === 'loading'}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="interest">{t.interestedIn}</label>
                  <select
                    id="interest" name="interest"
                    value={form.interest} onChange={handleChange}
                    disabled={status === 'loading'}
                  >
                    <option value="">{t.selectTopic}</option>
                    {t.interests.map(i => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message">{t.message} *</label>
                  <textarea
                    id="message" name="message" required rows={5}
                    placeholder={t.messagePlaceholder}
                    value={form.message} onChange={handleChange}
                    disabled={status === 'loading'}
                  />
                </div>

                <button
                  type="submit"
                  className="btn-primary contact-form__submit"
                  disabled={status === 'loading'}
                >
                  {status === 'loading' ? (
                    <span className="contact-form__spinner" />
                  ) : (
                    <><Send size={16} /> {t.send}</>
                  )}
                </button>

                <p className="contact-form__note">
                  {lang === 'ar'
                    ? `سيتم إرسال رسالتك إلى ${TO_EMAIL}`
                    : `Your message will be sent to ${TO_EMAIL}`}
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
